/** 应用外壳：侧栏导航 + 停手横条 + 顶栏 + 内容区。
 *
 * 布局与原型 `app.js` 的 `render()` 一致：停机横条压在页面顶部、滚不掉；
 * 顶栏左侧是口径指纹、右侧是状态灯。这里只多一样**后端未接线横条**——
 * 加载/错误态必须在全站可见，而不是只在某一屏里小声说一句。
 */

import { useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet } from "react-router";

import { useStatus } from "../api/queries";
import { describeError } from "../api/errors";
import { NAV } from "../lib/nav";
import { describeHalt, statusChips } from "../lib/halt";
import { short } from "../lib/format";
import type { StatusPayload } from "../api/types";

export function AppShell(): React.ReactNode {
  const status = useStatus();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <b>someo-keel</b>
          <span>操作台</span>
        </div>
        <nav className="nav">
          {NAV.map((group) => (
            <div className="nav-group" key={group.group}>
              <h4>{group.group}</h4>
              {group.items.map((item) => (
                <NavLink key={item.id} to={`/${item.id}`} className={({ isActive }) => (isActive ? "on" : "")}>
                  {item.label}
                  {item.wire === "wired" ? null : (
                    <span className="badge pill neutral" title={item.why}>
                      {item.wire === "candidate" ? "候选" : item.wire === "planned-card" ? "待开卡" : "未接线"}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <div className="main">
        {status.data?.halted === true ? <HaltBar status={status.data} /> : null}
        {status.isError ? <DeadBar error={status.error} /> : null}
        <TopBar status={status.data} pending={status.isPending} />
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function HaltBar({ status }: { status: StatusPayload }): React.ReactNode {
  const halt = describeHalt(status);
  return (
    <div className="haltbar" role="alert">
      <span className="dot" />
      <b>停机停手</b>
      <span>下单闸门{halt.gate}</span>
      <span className="why">{halt.reason ?? "（内核未给出原因）"}</span>
      <span style={{ flex: 1 }} />
      <span className="faint">禁止自动恢复</span>
      <NavLink className="btn sm" to="/halt">
        去停机屏 →
      </NavLink>
    </div>
  );
}

function DeadBar({ error }: { error: unknown }): React.ReactNode {
  const text = describeError(error);
  return (
    <div className="deadbar" role="alert">
      <b>{text.title}</b>
      <span className="why">{text.why}</span>
      <span style={{ flex: 1 }} />
      <span>这一栏消失之前，页面上的数字都不作数</span>
    </div>
  );
}

function TopBar({ status, pending }: { status: StatusPayload | undefined; pending: boolean }): React.ReactNode {
  const client = useQueryClient();
  return (
    <div className="topbar">
      <span className="statuschip">口径 {status ? status.config_digest : pending ? "读取中…" : "—"}</span>
      <span style={{ flex: 1 }} />
      {status ? (
        <>
          {statusChips(status).map((chip) => (
            <span className="statuschip" key={chip.text}>
              <span className={chip.tone === "ok" ? "dot" : `dot ${chip.tone}`} />
              {chip.text}
            </span>
          ))}
          <span className="statuschip">
            <span className="dot" />
            运行 {status.current_run ? short(status.current_run, 14) : "—"}
          </span>
        </>
      ) : null}
      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="btn sm"
        onClick={() => {
          void client.invalidateQueries();
        }}
      >
        重新取数
      </button>
      <span className="faint mono">{status ? (status.running ? `运行中 ${short(status.running, 14)}` : "空闲") : ""}</span>
    </div>
  );
}
